// 订单数据源配置相关API
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

// 查询键工厂
export const orderConfigKeys = {
  all: ['order-config'] as const,
  details: () => [...orderConfigKeys.all, 'detail'] as const,
  detail: (companyId: number) => [...orderConfigKeys.details(), companyId] as const,
}

// API端点常量
const ORDER_CONFIG_ENDPOINTS = {
  BASE: '/order-config',
  test: (companyId: number) => `/order-config/${companyId}/test`,
  save: (companyId: number) => `/order-config/${companyId}/save`,
  get: (companyId: number) => `/order-config/${companyId}`,
}

// 请求/响应类型定义
export interface StorageConfigRequest {
  type: string
  config: Record<string, any>
  is_default: boolean
  enabled: boolean
}

export interface TestConnectionRequest {
  config: StorageConfigRequest
}

export interface TestConnectionResponse {
  success: boolean
  healthy: boolean
  details?: Record<string, any>
  config_type?: string
  error?: string
}

export interface SaveConfigResponse {
  success: boolean
  config_id?: number
  operation?: string
  message?: string
  error?: string
}

export interface GetConfigResponse {
  success: boolean
  data?: Record<string, any>
  error?: string
}

// 测试连接
export const useTestOrderDataSourceConnection = () => {
  return useMutation({
    mutationFn: async ({ companyId, config }: { companyId: number; config: StorageConfigRequest }) => {
      // 使用适配后的API方法
      return await orderConfigApi.testConnection(companyId, config)
    },
  })
}

// 保存配置
export const useSaveOrderDataSourceConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ companyId, config }: { companyId: number; config: StorageConfigRequest }) => {
      // 使用适配后的API方法
      return await orderConfigApi.saveConfig(companyId, config)
    },
    onSuccess: (data, variables) => {
      // 使配置查询失效
      queryClient.invalidateQueries({ queryKey: orderConfigKeys.detail(variables.companyId) })
    },
  })
}

// 获取配置
export const useOrderDataSourceConfig = (companyId: number) => {
  return useQuery({
    queryKey: orderConfigKeys.detail(companyId),
    queryFn: async () => {
      // 使用适配后的API方法
      return await orderConfigApi.getConfig(companyId)
    },
    enabled: !!companyId,
  })
}

// 直接API调用（不使用React Query）
export const orderConfigApi = {
  // 测试连接（适配拦截器行为）
  testConnection: async (companyId: number, config: StorageConfigRequest): Promise<TestConnectionResponse> => {
    try {
      const response = await api.post<any>(ORDER_CONFIG_ENDPOINTS.test(companyId), { config });

      // 拦截器已剥离外层结构的情况（response是配置数据对象）
      if (response && typeof response === 'object' && response.healthy !== undefined) {
        // response已经是TestConnectionResponse结构
        return response;
      }

      // 拦截器返回的是配置数据，包装成TestConnectionResponse
      if (response && typeof response === 'object') {
        // 检查是否可能是健康检查结果
        if (response.success !== undefined) {
          return response as TestConnectionResponse;
        }
        // 默认认为连接测试成功
        return {
          success: true,
          healthy: true,
          details: response,
          config_type: config.type
        };
      }

      // 其他情况
      return { success: false, healthy: false, error: "连接测试响应格式异常" };
    } catch (error) {
      return { success: false, healthy: false, error: String(error) };
    }
  },

  // 保存配置（适配拦截器行为）
  saveConfig: async (companyId: number, config: StorageConfigRequest): Promise<SaveConfigResponse> => {
    try {
      const response = await api.post<any>(ORDER_CONFIG_ENDPOINTS.save(companyId), config);

      // 拦截器已剥离外层结构的情况
      if (response && typeof response === 'object') {
        // 如果响应已经有success字段，直接返回
        if (response.success !== undefined) {
          return response as SaveConfigResponse;
        }
        // 否则包装成成功响应
        return {
          success: true,
          config_id: response.id || response.config_id,
          operation: response.operation || "created",
          message: response.message || "配置保存成功"
        };
      }

      return { success: false, error: "保存配置响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },

  // 获取配置（适配拦截器行为）
  getConfig: async (companyId: number): Promise<GetConfigResponse> => {
    try {
      const response = await api.get<any>(ORDER_CONFIG_ENDPOINTS.get(companyId));

      // 拦截器已剥离data字段的情况（response就是配置数据对象）
      if (response && typeof response === 'object' && response.type !== undefined) {
        return { success: true, data: response };
      }

      // 如果响应已经有success字段，直接返回（完整响应结构）
      if (response && typeof response === 'object' && response.success !== undefined) {
        return response;
      }

      // 其他情况
      return { success: false, error: "配置响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },
}